import { useState, useRef } from "react";
import { useLocation } from "wouter";
import {
  Briefcase, Eye, EyeOff, Loader2, Lock, ChevronDown, ArrowRight,
  MessageCircle, FileText, Upload, Users, Building2, CheckCircle2,
  Target, Zap, TrendingUp, Video, Mail, FileCheck, BookOpen, Star,
} from "lucide-react";
import { PortalSwitcher } from "@/components/portal-switcher";
import { ForgotPasswordModal } from "@/pages/auth/forgot-password-modal";

const BASE = import.meta.env.BASE_URL;

export default function BankerLogin() {
  const [, navigate] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loginRef = useRef<HTMLDivElement>(null);
  const trainingRef = useRef<HTMLDivElement>(null);

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) => ref.current?.scrollIntoView({ behavior: "smooth" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${BASE}api/bankers/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (res.ok) {
        const d = await res.json();
        localStorage.setItem("banker_token", d.token);
        localStorage.setItem("banker_data", JSON.stringify(d.banker));
        // First-login or admin-reset path: bounce straight to change-password.
        if (d.mustChangePassword) {
          localStorage.setItem("banker_must_change_password", "true");
          navigate("/banker/change-password");
        } else {
          localStorage.removeItem("banker_must_change_password");
          navigate("/banker");
        }
      } else {
        const err = await res.json();
        setError(err.error || "Login failed");
      }
    } catch { setError("Unable to connect. Please try again."); }
    setLoading(false);
  };

  const responsibilities = [
    { icon: MessageCircle, title: "Respond to Meera's briefs", desc: "Every task in your queue is a synthesised investor ask. Read the brief, act within 24 hours." },
    { icon: Video, title: "Converse with founders", desc: "Zoom calls, phone calls, emails — whatever it takes to get the answer or arrange the introduction." },
    { icon: Upload, title: "Upload every artifact", desc: "Zoom transcripts, email threads, PDFs, Google Drive links — back into the panel. Meera reads them." },
    { icon: MessageCircle, title: "Use Meera as your wingwoman", desc: "Ask Meera anything — investor context, company facts, outreach drafts. She's scoped to your portfolio." },
    { icon: TrendingUp, title: "Move primary-ready investors", desc: "Flag ₹25L+ signals. Coordinate Favcy Advisors agreement. Own the end-to-end placement." },
    { icon: CheckCircle2, title: "Close the loop", desc: "Once artifact is uploaded, Meera synthesises and Kavya responds to the investor. Your work is visible." },
  ];

  const training = [
    { stage: "1", title: "Read the task", body: "Every task includes: the investor's original ask (verbatim), Meera's brief (what you need to know), and priority. Never skip the brief." },
    { stage: "2", title: "Claim & set status", body: "Mark In Progress once you start. If you need to reach the founder, move to Awaiting Founder. This unblocks Kavya from giving stale holding responses to the investor." },
    { stage: "3", title: "Do the human work", body: "Zoom with the founder, email the CFO, dig into the data room. Favcy Select is premium — response quality > response speed." },
    { stage: "4", title: "Upload the artifact", body: "Three ways: (a) paste a Google Drive / Docs link — we fetch contents, (b) attach a PDF, (c) paste raw Zoom transcript or email thread. Title it clearly." },
    { stage: "5", title: "Let Meera synthesise", body: "She auto-summarises the artifact and pushes the key findings back to Kavya. The investor gets a notification in their inbox flagged 'Favcy Advisors'." },
    { stage: "6", title: "Mark closed", body: "Task moves to closed state. Your performance metrics update. Your coverage relationship strengthens." },
  ];

  const rules = [
    "Never promise primary investment without Favcy Advisors' written agreement in place.",
    "Every investor-facing commitment must be logged as an artifact — no exceptions.",
    "Minimum ₹25 lacs for primary tickets (SEBI). Accredited status must be verified.",
    "All founder conversations belong in the panel. Do not treat WhatsApp as system-of-record.",
    "If Meera flags a hot investor, act within 24 hours. Cold investors can wait 72.",
    "Respect coverage boundaries — only act on investors assigned to you. View-only for the rest.",
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-y-auto">
      <div className="fixed inset-0 pointer-events-none opacity-[0.08]" style={{
        backgroundImage: "radial-gradient(circle at 15% 20%, #FF5C00 0%, transparent 45%), radial-gradient(circle at 85% 80%, #FF5C00 0%, transparent 35%)"
      }} />

      <div className="relative">
        <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/[0.08]">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center">
                <Briefcase className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-lg leading-none">Favcy Advisors</p>
                <p className="text-white/50 text-[9px] font-mono uppercase tracking-[0.2em]">Investment Banker Panel</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => scrollTo(trainingRef)} className="hidden sm:block text-white/60 hover:text-white text-sm px-3 py-2">Training</button>
              <button onClick={() => scrollTo(loginRef)} className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors">Sign In</button>
            </div>
          </div>
          <div className="border-t border-white/[0.06] bg-[#0A0A0A]/60">
            <div className="max-w-6xl mx-auto px-6 py-2">
              <PortalSwitcher current="banker" tone="dark" />
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="max-w-5xl mx-auto px-6 pt-20 pb-16 text-center">
          <div className="inline-flex items-center gap-2 bg-white/[0.06] border border-white/15 rounded-full px-4 py-1.5 mb-8">
            <Star className="w-3.5 h-3.5 text-[#FF5C00]" />
            <span className="text-white/70 text-xs font-medium">The operating system for Favcy's Investment Banking team</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] max-w-3xl mx-auto">
            You convert <span className="text-[#FF5C00]">investor intent</span> into <span className="text-[#FF5C00]">capital</span>.
          </h1>
          <p className="text-white/70 text-lg md:text-xl mt-6 max-w-2xl mx-auto leading-relaxed">
            Kavya talks to investors. Meera synthesises. You act. Every task in your queue is a real investor asking for something real. Your job is to close the loop.
          </p>
          <div className="flex items-center justify-center gap-3 mt-10 flex-wrap">
            <button onClick={() => scrollTo(loginRef)} className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-8 py-3.5 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2">
              Sign in to your panel <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => scrollTo(trainingRef)} className="border border-white/20 hover:border-white/40 text-white/80 hover:text-white px-8 py-3.5 rounded-xl text-sm font-medium flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Read the training pack
            </button>
          </div>
          <button onClick={() => scrollTo(trainingRef)} className="mt-14 animate-bounce text-white/30 hover:text-white/50 transition-colors">
            <ChevronDown className="w-6 h-6 mx-auto" />
          </button>
        </section>

        {/* Value Prop Grid */}
        <section className="max-w-6xl mx-auto px-6 py-16 border-y border-white/[0.08] bg-white/[0.02]">
          <div className="text-center mb-12">
            <p className="text-[#FF5C00] text-xs font-semibold uppercase tracking-widest mb-3">The Favcy Advisors Loop</p>
            <h2 className="text-3xl md:text-4xl font-bold">How the system works around you</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              { num: "1", icon: MessageCircle, color: "orange", title: "Kavya hears", body: "The AI front door talks to every investor. She captures questions, intent, primary signals." },
              { num: "2", icon: Target, color: "emerald", title: "Meera routes", body: "She extracts actionable asks and pushes them to you as tasks — with full context and a brief." },
              { num: "3", icon: Briefcase, color: "blue", title: "You close", body: "Call the founder, gather the answer, upload the artifact. Meera loops it back to the investor." },
            ].map((s) => (
              <div key={s.num} className="relative bg-white/[0.04] border border-white/[0.1] rounded-2xl p-6">
                <div className={`w-10 h-10 rounded-full bg-${s.color}-500/15 border border-${s.color}-500/25 flex items-center justify-center mb-4`}>
                  <s.icon className={`w-5 h-5 text-${s.color}-400`} />
                </div>
                <p className="text-white/40 text-[10px] font-mono mb-1">STAGE {s.num}</p>
                <h3 className="text-white font-bold text-lg mb-2">{s.title}</h3>
                <p className="text-white/65 text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Responsibilities */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <p className="text-[#FF5C00] text-xs font-semibold uppercase tracking-widest mb-3">Your Mandate</p>
            <h2 className="text-3xl md:text-4xl font-bold">What's expected of you</h2>
            <p className="text-white/60 text-sm mt-3 max-w-lg mx-auto">You are the human glue between investors and founders. The panel gives you leverage — use it.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {responsibilities.map((r, i) => (
              <div key={i} className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-5 hover:border-[#FF5C00]/25 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-[#FF5C00]/15 border border-[#FF5C00]/20 flex items-center justify-center mb-3">
                  <r.icon className="w-4 h-4 text-[#FF5C00]" />
                </div>
                <h3 className="text-white font-semibold text-sm mb-1.5">{r.title}</h3>
                <p className="text-white/55 text-xs leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Training Pack */}
        <section ref={trainingRef} className="border-y border-white/[0.08] bg-white/[0.02]">
          <div className="max-w-5xl mx-auto px-6 py-20">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#FF5C00]/10 border border-[#FF5C00]/25 rounded-full px-4 py-1.5 mb-4">
                <BookOpen className="w-3.5 h-3.5 text-[#FF5C00]" />
                <span className="text-[#FF5C00] text-xs font-semibold uppercase tracking-wider">Training Pack</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">Your 6-step playbook</h2>
              <p className="text-white/60 text-sm mt-3 max-w-lg mx-auto">The exact workflow from receiving a task to closing it. Read this once — then keep it bookmarked.</p>
            </div>
            <div className="space-y-3 max-w-3xl mx-auto">
              {training.map((t, i) => (
                <div key={i} className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#FF5C00]/15 border border-[#FF5C00]/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#FF5C00] font-bold text-sm">{t.stage}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-base mb-1">{t.title}</h3>
                    <p className="text-white/65 text-sm leading-relaxed">{t.body}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Rules */}
            <div className="mt-16 bg-gradient-to-br from-red-500/[0.05] to-[#FF5C00]/[0.05] border border-red-500/20 rounded-2xl p-8 max-w-3xl mx-auto">
              <div className="flex items-center gap-2 mb-5">
                <FileCheck className="w-5 h-5 text-red-400" />
                <h3 className="text-white font-bold text-lg">Non-negotiable rules</h3>
              </div>
              <ul className="space-y-3">
                {rules.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    <p className="text-white/75 leading-relaxed">{r}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 bg-white/[0.04] border border-[#FF5C00]/25 rounded-2xl p-6 max-w-3xl mx-auto text-center">
              <Zap className="w-6 h-6 text-[#FF5C00] mx-auto mb-3" />
              <p className="text-white font-semibold text-base mb-1.5">Meera is always on your side.</p>
              <p className="text-white/65 text-sm max-w-md mx-auto">Tap "Ask Meera" any time — she has your investors, your companies, your tasks, and your artifacts at her fingertips. Use her as your research associate.</p>
            </div>
          </div>
        </section>

        {/* Login */}
        <section ref={loginRef} className="max-w-md mx-auto px-6 py-20">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF5C00] to-[#E55200] mb-4">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold">Sign in to your panel</h2>
            <p className="text-white/60 text-sm mt-2">Favcy Advisors — Investment Banker access</p>
          </div>
          <form onSubmit={handleSubmit} className="bg-white/[0.04] border border-white/[0.12] rounded-2xl p-8 space-y-5">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm text-center">{error}</div>
            )}
            <div>
              <label className="block text-white/70 text-xs uppercase tracking-wider mb-2">Work Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50 focus:ring-1 focus:ring-[#FF5C00]/20"
                placeholder="you@favcy.in" />
            </div>
            <div>
              <label className="block text-white/70 text-xs uppercase tracking-wider mb-2">Password</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-4 py-3 pr-10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50"
                  placeholder="Enter your password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="mt-2 text-right">
                <button type="button" onClick={() => setForgotOpen(true)} className="text-[11px] text-white/40 hover:text-[#FF5C00] transition-colors">
                  Forgot password?
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white py-3 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Signing in...</> : <>Access my panel <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-white/40 text-[11px] text-center">Access is provisioned by the Favcy admin team. If you don't have credentials, request them from the master admin.</p>
          </form>
        </section>

        <footer className="border-t border-white/[0.08] py-6 text-center space-y-3">
          <PortalSwitcher current="banker" tone="dark" />
          <p className="text-white/30 text-xs">© {new Date().getFullYear()} Favcy Venture Builders · Favcy Advisors · Favcy Select</p>
        </footer>
      </div>

      <ForgotPasswordModal audience="banker" open={forgotOpen} initialEmail={email} onClose={() => setForgotOpen(false)} />
    </div>
  );
}
